import { useRef, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { formatPercent, formatCurrency } from "../../../utils/formatNumber";
import useColumnSelector from "../../../hooks/useColumnSelector";
import ReactTable from "react-table-6";
import { Card, Row, Col, Table } from "react-bootstrap";
import ReactTablePagination from "../../../components/custom/growth/ProductsCVTablePagination";
import TrComponent from "../../../components/custom/merchandise/TrComponent";
import ExpanderCell from "../../../components/custom/merchandise/cells/ExpanderCell";
import { useFetch } from "../../../hooks/api";
import moment from "moment";
import Loading from "../../../components/core/blocks/Loading";
import { Link } from "@tanstack/react-router";
import { useDateFormatter } from "../../../i18n/useDateFormatter";

const ExpandedContent = ({ row }) => {
  const { t } = useTranslation(["merchandising"]);
  const { data: couponStats, isLoading: isCouponStatsLoading } = useFetch(
    ["couponMetrics", row?.original.id],
    `/merchandise/coupons_metrics/?report_id=${row?.original.id}`,
    {},
    {
      select: (data) => data?.data,
    },
  );

  if (isCouponStatsLoading) {
    return <Loading height={"300px"} fullPage={false} />;
  }

  if (couponStats?.results?.length === 0) {
    return (
      <div className="p5- m-5 fw-bold text-center">
        <em>{t(($) => $.merchandising["There are no associated coupons for this product"])}</em>
      </div>
    );
  }

  const stats = couponStats.results[0];

  const redemtionRate = stats.clips ? stats.redemptions / stats.clips : 0;
  const salesPerRedemption = stats.redemptions ? stats.sales / stats.redemptions : 0;
  const discountPercentOfSales = stats.sales ? stats.total_discount / stats.sales : 0;
  const costPerRedemption = stats.redemptions ? stats.budget_spent / stats.redemptions : 0;

  return (
    <div style={{ backgroundColor: "#efefef" }}>
      <Card className="fs-5 mb-0 p-3">
        <Card.Body>
          <Row>
            <Col xs={12}>
              <h6>{t(($) => $.merchandising["Coupon Metrics"])}</h6>
            </Col>

            <Col xs={12} className="pt-3">
              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th className="text-center">{t(($) => $.merchandising["Budget"])}</th>
                    <th className="text-center">{t(($) => $.merchandising["Budget % Used"])}</th>
                    <th className="text-center">{t(($) => $.merchandising["Budget Remaining"])}</th>
                    <th className="text-center">{t(($) => $.merchandising["Budget Spent"])}</th>
                    <th className="text-center">{t(($) => $.merchandising["Clips"])}</th>
                    <th className="text-center">{t(($) => $.merchandising["Redemptions"])}</th>
                    <th className="text-center">{t(($) => $.merchandising["Sales"])}</th>
                    <th className="text-center">{t(($) => $.merchandising["Total Discount"])}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="text-center">
                      {formatCurrency(stats.budget, {
                        marketPlace: row?.original.marketplace,
                      })}
                    </td>
                    <td className="text-center">{formatPercent(stats.budget_percentage_used / 100.0)}</td>
                    <td className="text-center">
                      {formatCurrency(stats.budget_remaining, {
                        marketPlace: row?.original.marketplace,
                      })}
                    </td>
                    <td className="text-center">
                      {formatCurrency(stats.budget_spent, {
                        marketPlace: row?.original.marketplace,
                      })}
                    </td>
                    <td className="text-center">{stats.clips}</td>
                    <td className="text-center">{stats.redemptions}</td>
                    <td className="text-center">
                      {formatCurrency(stats.sales, {
                        marketPlace: row?.original.marketplace,
                      })}
                    </td>
                    <td className="text-center">
                      {formatCurrency(stats.total_discount, {
                        marketPlace: row?.original.marketplace,
                      })}
                    </td>
                  </tr>
                </tbody>
              </Table>
            </Col>

            <Col xs={12} className="pt-5">
              <h6>{t(($) => $.merchandising["Coupon Insights"])}</h6>
            </Col>

            <Col xs={12} className="pt-3">
              <Table striped bordered hover>
                <tbody>
                  <tr>
                    <th className="text-left" style={{ width: "200px" }}>
                      {t(($) => $.merchandising["Redemption Rate"])}
                    </th>
                    <td className="text-left">
                      {t(
                        ($) =>
                          $.merchandising[
                            "The coupon achieved a {rate} redemption rate, showing the proportion of clips that led to redemptions."
                          ],
                        {
                          rate: formatPercent(redemtionRate, { decimalPlaces: 0 }),
                        },
                      )}
                    </td>
                  </tr>
                  <tr>
                    <th className="text-left">{t(($) => $.merchandising["Sales per Redemption"])}</th>
                    <td className="text-left">
                      {t(
                        ($) =>
                          $.merchandising[
                            "On average, each coupon redemption resulted in {amount} in sales, indicating the revenue impact per use."
                          ],
                        {
                          amount: formatCurrency(salesPerRedemption, { marketPlace: row?.original.marketplace }),
                        },
                      )}
                    </td>
                  </tr>
                  <tr>
                    <th className="text-left">{t(($) => $.merchandising["Discount % of Sales"])}</th>
                    <td className="text-left">
                      {t(
                        ($) =>
                          $.merchandising[
                            "The discount provided accounts for {percent} of the total sales, representing the portion of sales value reduced by the coupon."
                          ],
                        {
                          percent: formatPercent(discountPercentOfSales, { decimalPlaces: 0 }),
                        },
                      )}
                    </td>
                  </tr>
                  <tr>
                    <th className="text-left">{t(($) => $.merchandising["Cost Per Redemption"])}</th>
                    <td className="text-left">
                      <div>
                        {t(
                          ($) =>
                            $.merchandising[
                              "So far, {percentSpent} of the budget has been spent, equating to {costPerRedemption} per redemption in cost."
                            ],
                          {
                            percentSpent: formatPercent(stats.budget_percentage_used / 100.0),
                            costPerRedemption: formatCurrency(costPerRedemption, {
                              marketPlace: row?.original.marketplace,
                            }),
                          },
                        )}
                      </div>
                    </td>
                  </tr>
                </tbody>
              </Table>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </div>
  );
};

const makeExpanderCell = (accessor, setExpanded) => {
  return ({ row, value }) => {
    const id = accessor(row?._original);
    return <ExpanderCell row={row} value={id} isExpanded={value} setIsExpanded={setExpanded} />;
  };
};

const CouponReport = ({ data, marketPlace, asinReport }) => {
  const { t } = useTranslation(["merchandising"]);
  const dateFmt = useDateFormatter("medium");
  const [colToggle] = useState();
  const [expanded, setExpanded] = useState({});

  const tableData = useMemo(() => {
    if (!data) return [];

    return data?.results.map((row) => ({
      ...row,
      isExpanded: row.id === expanded ? row.id : null,
    }));
  }, [data, expanded]);

  const tableRef = useRef();
  const [columns, columnSelectorProps] = useColumnSelector(() => {
    const columns = [
      {
        id: "expander",
        Header: "",
        accessor: "isExpanded",
        width: 50,
        Cell: makeExpanderCell((row) => row.id, setExpanded),
      },
      {
        id: "Coupon Title",
        label: t(($) => $.merchandising["Coupon Title"]),
        Header: () => <span>{t(($) => $.merchandising["Coupon Title"])}</span>,
        headerClassName: "wordwrap",
        headerStyle: { textAlign: "left" },
        style: { textAlign: "left" },
        width: 400,
        accessor: "name",
        Cell: (props) => {
          return (
            <div>
              <div className="fw-bold">
                <Link to="/user/merchandising/coupons/$coupon" params={{ coupon: String(props.original.id) }}>
                  {props.value}
                </Link>
              </div>
              <div style={{ fontSize: "11px" }}>
                <em>
                  {props.original?.website_message} - ({props.original.coupon_id})
                </em>
              </div>
            </div>
          );
        },
      },
      {
        id: "StartTime",
        label: t(($) => $.merchandising["Start Time"]),
        Header: () => <span>{t(($) => $.merchandising["Start Time"])}</span>,
        headerClassName: "wordwrap",
        headerStyle: { textAlign: "left" },
        style: { textAlign: "left" },
        accessor: "start_time",
        Cell: (props) => {
          return <div>{dateFmt.format(moment(props.value).toDate())}</div>;
        },
      },
      {
        id: "EndTime",
        label: t(($) => $.merchandising["End Time"]),
        Header: () => <span>{t(($) => $.merchandising["End Time"])}</span>,
        headerClassName: "wordwrap",
        headerStyle: { textAlign: "left" },
        style: { textAlign: "left" },
        accessor: "end_time",
        Cell: (props) => {
          return <div>{dateFmt.format(moment(props.value).toDate())}</div>;
        },
      },
      {
        id: "DiscountAmount",
        label: t(($) => $.merchandising["Discount Amount"]),
        Header: () => <span>{t(($) => $.merchandising["Discount Amount"])}</span>,
        headerClassName: "wordwrap",
        headerStyle: { textAlign: "left" },
        style: { textAlign: "left" },
        accessor: "discount_amount",
        Cell: (props) => {
          if (props.original.discount_type === "PERCENT_OFF_LIST_PRICE") {
            return (
              <div>
                {formatPercent(parseFloat(props.value) / 100.0, {
                  decimalPlaces: 0,
                })}
              </div>
            );
          }
          return (
            <div>
              {formatCurrency(props.value, {
                marketPlace: marketPlace,
              })}
            </div>
          );
        },
      },
    ];

    const asinColumns = [
      {
        id: "Units",
        label: t(($) => $.merchandising["Units"]),
        Header: () => <span>{t(($) => $.merchandising["Units"])}</span>,
        headerClassName: "wordwrap",
        headerStyle: { textAlign: "left" },
        style: { textAlign: "left" },
        accessor: "order_info.count",
        Cell: (props) => {
          return <div>{props.value}</div>;
        },
      },
      {
        id: "Sales",
        label: t(($) => $.merchandising["Promo Sales"]),
        Header: () => <span>{t(($) => $.merchandising["Promo Sales"])}</span>,
        headerClassName: "wordwrap",
        headerStyle: { textAlign: "left" },
        style: { textAlign: "left" },
        accessor: "order_info.sales",
        Cell: (props) => {
          return (
            <div>
              {formatCurrency(props.value, {
                marketPlace: marketPlace,
              })}
            </div>
          );
        },
      },
      {
        id: "PromoDiscount",
        label: t(($) => $.merchandising["Promo Discount"]),
        Header: () => <span>{t(($) => $.merchandising["Promo Discount"])}</span>,
        headerClassName: "wordwrap",
        headerStyle: { textAlign: "left" },
        style: { textAlign: "left" },
        accessor: "order_info.promo_discount",
        Cell: (props) => {
          return (
            <div>
              {formatCurrency(props.value, {
                marketPlace: marketPlace,
              })}
            </div>
          );
        },
      },
      {
        id: "discount_percent_of_sales",
        label: t(($) => $.merchandising["Discount % of Sales"]),
        Header: () => <span>{t(($) => $.merchandising["Discount % of Sales"])}</span>,
        headerClassName: "wordwrap",
        headerStyle: { textAlign: "left" },
        style: { textAlign: "left" },
        accessor: (props) =>
          props.order_info.before_sales ? props.order_info.promo_discount / props.order_info.before_sales : 0,
        Cell: (props) => {
          return <div>{formatPercent(parseFloat(props.value))}</div>;
        },
      },
    ];

    const moreColumns = [
      {
        id: "Sales",
        label: t(($) => $.merchandising["Sales"]),
        Header: () => <span>{t(($) => $.merchandising["Sales"])}</span>,
        headerClassName: "wordwrap",
        headerStyle: { textAlign: "left" },
        style: { textAlign: "left" },
        accessor: "metrics.sales",
        Cell: (props) => {
          return (
            <div>
              {formatCurrency(parseFloat(props.value), {
                marketPlace: marketPlace,
              })}
            </div>
          );
        },
      },
      {
        id: "Clips",
        label: t(($) => $.merchandising["Clips"]),
        Header: () => <span>{t(($) => $.merchandising["Clips"])}</span>,
        headerClassName: "wordwrap",
        headerStyle: { textAlign: "left" },
        style: { textAlign: "left" },
        accessor: "metrics.clips",
        Cell: (props) => {
          return <div>{props.value}</div>;
        },
      },
      {
        id: "Redemptions",
        label: t(($) => $.merchandising["Redemptions"]),
        Header: () => <span>{t(($) => $.merchandising["Redemptions"])}</span>,
        headerClassName: "wordwrap",
        headerStyle: { textAlign: "left" },
        style: { textAlign: "left" },
        accessor: "metrics.redemptions",
        Cell: (props) => {
          return <div>{props.value}</div>;
        },
      },
      {
        id: "redemption_rate",
        label: t(($) => $.merchandising["Redemption Rate"]),
        Header: () => <span>{t(($) => $.merchandising["Redemption Rate"])}</span>,
        headerClassName: "wordwrap",
        headerStyle: { textAlign: "left" },
        style: { textAlign: "left" },
        accessor: "metrics.redemption_rate",
        Cell: (props) => {
          return (
            <div>
              {formatPercent(parseFloat(props.value), {
                decimalPlaces: 1,
              })}
            </div>
          );
        },
      },
      {
        id: "sales_per_redemption",
        label: t(($) => $.merchandising["Sales per Redemption"]),
        Header: () => <span>{t(($) => $.merchandising["Sales per Redemption"])}</span>,
        headerClassName: "wordwrap",
        headerStyle: { textAlign: "left" },
        style: { textAlign: "left" },
        accessor: "metrics.sales_per_redemption",
        Cell: (props) => {
          return (
            <div>
              {formatCurrency(parseFloat(props.value), {
                marketPlace: marketPlace,
              })}
            </div>
          );
        },
      },
      {
        id: "discount_percent_of_sales",
        label: t(($) => $.merchandising["ACOS"]),
        Header: () => <span>{t(($) => $.merchandising["ACOS"])}</span>,
        headerClassName: "wordwrap",
        headerStyle: { textAlign: "left" },
        style: { textAlign: "left" },
        accessor: "metrics.discount_percent_of_sales",
        Cell: (props) => {
          return <div>{formatPercent(parseFloat(props.value))}</div>;
        },
      },
      {
        id: "budget_efficiency",
        label: t(($) => $.merchandising["Cost per Redemption"]),
        Header: () => <span>{t(($) => $.merchandising["Cost per Redemption"])}</span>,
        headerClassName: "wordwrap",
        headerStyle: { textAlign: "left" },
        style: { textAlign: "left" },
        accessor: "metrics.cost_per_redemption",
        Cell: (props) => {
          return (
            <div>
              {formatCurrency(parseFloat(props.value), {
                marketPlace: marketPlace,
              })}
            </div>
          );
        },
      },
    ];

    if (asinReport) {
      columns.push(...asinColumns);
    } else {
      columns.push(...moreColumns);
    }

    return columns;
  }, [colToggle, t]);

  if (data?.results?.length === 0) {
    return (
      <div className="p5- m-5 fw-bold text-center">
        <em>{t(($) => $.merchandising["There are no associated coupons for this product"])}</em>
      </div>
    );
  }

  return (
    <ReactTable
      ref={tableRef}
      data={tableData}
      className="coupon-table"
      PaginationComponent={(props) => {
        return (
          <ReactTablePagination
            {...props}
            table={"coupon-table"}
            showFilters={false}
            style={{ marginBottom: "2rem" }}
            carouselLayout={true}
            hideFilters={true}
            columnSelectorProps={columnSelectorProps}
            hideSearch={true}
          />
        );
      }}
      TrComponent={TrComponent}
      getTrProps={(table, row) => {
        const expandedChildren = <ExpandedContent row={row} />;
        return { row, expandedChildren };
      }}
      columns={columns}
      defaultPageSize={20}
      NoDataComponent={() => null}
      minRows={0}
      showPaginationTop
      showPaginationBottom={false}
      filterable={false}
    />
  );
};

export default CouponReport;
